import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Alert,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Shield,
  Security,
  Visibility,
  DataUsage,
  Settings,
  CheckCircle,
  Person,
  Lock,
  Share,
  Cookie,
  Email,
  Storage,
  Delete,
  Update,
  ContactSupport,
  Business,
  Language,
  Warning
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  const handleBackToRegister = () => {
    navigate('/register');
  };

  const dataCollection = [
    {
      title: "Personal Information",
      icon: <Person color="primary" />,
      data: [
        "Full name (first and last name)",
        "Email address for account creation and communication",
        "Mobile phone number for verification and notifications",
        "Trading account numbers (XM Global, Weltrade)",
        "Trading preferences and experience level",
        "Profile information and preferences"
      ]
    },
    {
      title: "Technical Data",
      icon: <DataUsage color="primary" />,
      data: [
        "IP address and geolocation information",
        "Browser type, version, and operating system",
        "Device information and screen resolution",
        "Website usage patterns and navigation behavior",
        "Login timestamps and session duration",
        "Performance metrics and error logs"
      ]
    },
    {
      title: "Trading Data",
      icon: <Business color="primary" />,
      data: [
        "Competition participation records",
        "Trading performance and statistics",
        "Points, rankings, and achievements",
        "Prize eligibility and distribution records",
        "Account verification status",
        "Affiliate program participation data"
      ]
    }
  ];

  const dataUsage = [
    {
      title: "Service Provision",
      icon: <Settings color="primary" />,
      purposes: [
        "Creating and managing your FX Wars account",
        "Processing competition registrations and entries",
        "Calculating points, rankings, and leaderboards",
        "Verifying trading account affiliations",
        "Distributing prizes and managing awards",
        "Providing customer support and assistance"
      ]
    },
    {
      title: "Communication",
      icon: <Email color="primary" />,
      purposes: [
        "Sending important account notifications",
        "Competition updates and announcements",
        "Prize distribution communications",
        "Marketing newsletters (with consent)",
        "Security alerts and account warnings",
        "Customer service responses"
      ]
    },
    {
      title: "Platform Improvement",
      icon: <Update color="primary" />,
      purposes: [
        "Analyzing user behavior to improve services",
        "Identifying and fixing technical issues",
        "Developing new features and functionality",
        "Optimizing website performance",
        "Conducting security monitoring",
        "Generating anonymized usage statistics"
      ]
    }
  ];

  const userRights = [
    {
      right: "Access",
      icon: <Visibility color="primary" />,
      description: "Request a copy of all personal data we hold about you"
    },
    {
      right: "Rectification",
      icon: <Update color="primary" />,
      description: "Request correction of inaccurate or incomplete personal data"
    },
    {
      right: "Erasure",
      icon: <Delete color="primary" />,
      description: "Request deletion of your personal data (right to be forgotten)"
    },
    {
      right: "Portability",
      icon: <Share color="primary" />,
      description: "Request transfer of your data to another service provider"
    },
    {
      right: "Restriction",
      icon: <Lock color="primary" />,
      description: "Request limitation of processing of your personal data"
    },
    {
      right: "Objection",
      icon: <Warning color="primary" />,
      description: "Object to processing of your data for specific purposes"
    }
  ];

  const cookieTypes = [
    { type: "Essential", purpose: "Required for basic website functionality", duration: "Session", required: true },
    { type: "Analytics", purpose: "Help us understand how visitors use our website", duration: "2 years", required: false },
    { type: "Marketing", purpose: "Used to deliver relevant advertisements", duration: "1 year", required: false },
    { type: "Preferences", purpose: "Remember your settings and preferences", duration: "1 year", required: false }
  ];

  return (
    <Box sx={{ pt: 12, pb: 8, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box textAlign="center" mb={6}>
            <Typography variant="h2" gutterBottom sx={{ 
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              Privacy Policy
            </Typography>
            <Typography variant="h5" color="text.secondary" mb={2}>
              FX Wars Trading Competition Platform
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
              Your privacy is important to us. This policy explains how we collect, use, and protect 
              your personal information when you use our trading competition platform.
            </Typography>
          </Box>
        </motion.div>

        {/* Last Updated */}
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            <strong>Last Updated:</strong> January 2025 | 
            <strong> Effective Date:</strong> January 1, 2025 | 
            <strong> Version:</strong> 1.0
          </Typography>
        </Alert>

        {/* Quick Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <Paper sx={{ p: 3, mb: 6, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <Typography variant="h4" gutterBottom textAlign="center">
              🔒 Privacy at a Glance
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Shield sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Data Protection</Typography>
                  <Typography variant="body2">Industry-standard security</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Person sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Your Rights</Typography>
                  <Typography variant="body2">Full control over your data</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <Lock sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">Minimal Collection</Typography>
                  <Typography variant="body2">Only necessary information</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box textAlign="center">
                  <ContactSupport sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h6" fontWeight="bold">24/7 Support</Typography>
                  <Typography variant="body2">Always here to help</Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Data Collection Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Storage color="primary" sx={{ fontSize: 40 }} />
              What Information We Collect
            </Typography>
            <Grid container spacing={4}>
              {dataCollection.map((section, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' } }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        {section.icon}
                        <Typography variant="h6" fontWeight="bold" ml={1}>
                          {section.title}
                        </Typography>
                      </Box>
                      <List dense>
                        {section.data.map((item, itemIndex) => (
                          <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 20 }}>
                              <CheckCircle sx={{ fontSize: 16, color: '#4CAF50' }} />
                            </ListItemIcon>
                            <ListItemText primary={item} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        <Divider sx={{ my: 6 }} />

        {/* Data Usage Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <DataUsage color="primary" sx={{ fontSize: 40 }} />
              How We Use Your Information
            </Typography>
            <Grid container spacing={4}>
              {dataUsage.map((section, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: 'transform 0.2s' } }}>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        {section.icon}
                        <Typography variant="h6" fontWeight="bold" ml={1}>
                          {section.title}
                        </Typography>
                      </Box>
                      <List dense>
                        {section.purposes.map((purpose, purposeIndex) => (
                          <ListItem key={purposeIndex} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 20 }}>
                              <CheckCircle sx={{ fontSize: 16, color: '#2196F3' }} />
                            </ListItemIcon>
                            <ListItemText primary={purpose} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>

        <Divider sx={{ my: 6 }} />

        {/* Data Sharing and Disclosure */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Share color="primary" sx={{ fontSize: 40 }} />
              Data Sharing & Disclosure
            </Typography>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  When We Share Your Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><Business color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Partner Brokers" 
                      secondary="We share trading account numbers with XM Global and Weltrade only for affiliation verification purposes"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Security color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Legal Requirements" 
                      secondary="When required by law, court order, or government request"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Shield color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Service Providers" 
                      secondary="With trusted third-party service providers who assist in platform operations (under strict confidentiality agreements)"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Warning color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Safety & Security" 
                      secondary="To protect the rights, property, or safety of FX Wars, users, or the public"
                    />
                  </ListItem>
                </List>
                
                <Alert severity="success" sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    We Never Sell Your Data
                  </Typography>
                  <Typography variant="body2">
                    We do not sell, rent, or trade your personal information to third parties for marketing purposes.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Box>
        </motion.div>

        {/* Your Rights Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Person color="primary" sx={{ fontSize: 40 }} />
              Your Privacy Rights
            </Typography>
            <Grid container spacing={3}>
              {userRights.map((right, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
                    <CardContent>
                      {right.icon}
                      <Typography variant="h6" fontWeight="bold" mt={1} mb={1}>
                        {right.right}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {right.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Button 
                variant="contained" 
                onClick={() => navigate('/contact')}
                sx={{ mr: 2 }}
              >
                Exercise Your Rights
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Contact our support team to exercise any of your privacy rights. We'll respond within 30 days.
              </Typography>
            </Box>
          </Box>
        </motion.div>

        {/* Cookies and Tracking */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Box mb={6}>
            <Typography variant="h3" gutterBottom textAlign="center" mb={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <Cookie color="primary" sx={{ fontSize: 40 }} />
              Cookies & Tracking Technologies
            </Typography>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">
                  How We Use Cookies
                </Typography>
                <Typography variant="body1" paragraph>
                  We use cookies and similar tracking technologies to enhance your experience on our platform. 
                  Below is a breakdown of the types of cookies we use:
                </Typography>
                
                <TableContainer component={Paper} sx={{ mt: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Cookie Type</strong></TableCell>
                        <TableCell><strong>Purpose</strong></TableCell>
                        <TableCell><strong>Duration</strong></TableCell>
                        <TableCell><strong>Required</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cookieTypes.map((cookie, index) => (
                        <TableRow key={index}>
                          <TableCell>{cookie.type}</TableCell>
                          <TableCell>{cookie.purpose}</TableCell>
                          <TableCell>{cookie.duration}</TableCell>
                          <TableCell>
                            <Typography color={cookie.required ? 'error' : 'success'}>
                              {cookie.required ? 'Yes' : 'No'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Cookie Management
                  </Typography>
                  <Typography variant="body2">
                    You can control cookies through your browser settings. However, disabling essential cookies 
                    may affect the functionality of our platform.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Box>
        </motion.div>

        {/* Data Security */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <Box mb={6}>
            <Card sx={{ background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)', border: '2px solid #4caf50' }}>
              <CardContent>
                <Typography variant="h4" gutterBottom textAlign="center" color="success.main" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Security sx={{ fontSize: 40 }} />
                  Data Security & Protection
                </Typography>
                <Grid container spacing={3} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6}>
                    <List>
                      <ListItem>
                        <ListItemIcon><Lock color="success" /></ListItemIcon>
                        <ListItemText primary="SSL/TLS encryption for all data transmission" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Shield color="success" /></ListItemIcon>
                        <ListItemText primary="Regular security audits and vulnerability assessments" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Storage color="success" /></ListItemIcon>
                        <ListItemText primary="Secure cloud infrastructure with backup systems" />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <List>
                      <ListItem>
                        <ListItemIcon><Person color="success" /></ListItemIcon>
                        <ListItemText primary="Access controls and employee training programs" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Update color="success" /></ListItemIcon>
                        <ListItemText primary="Regular software updates and security patches" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><Warning color="success" /></ListItemIcon>
                        <ListItemText primary="Incident response and breach notification procedures" />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </motion.div>

        {/* International Transfers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Box mb={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Language color="primary" />
                  International Data Transfers
                </Typography>
                <Typography variant="body1" paragraph>
                  Your information may be transferred to and processed in countries other than your own. 
                  We ensure that such transfers comply with applicable data protection laws through:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                    <ListItemText primary="Adequacy decisions by relevant authorities" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                    <ListItemText primary="Standard contractual clauses approved by data protection authorities" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                    <ListItemText primary="Certification schemes and codes of conduct" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Box>
        </motion.div>

        {/* Data Retention */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
        >
          <Box mb={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Storage color="primary" />
                  Data Retention
                </Typography>
                <Typography variant="body1" paragraph>
                  We retain your personal information only as long as necessary for the purposes outlined in this policy:
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: '#f5f5f5' }}>
                      <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>Account Data</Typography>
                        <Typography variant="body2">Retained while your account is active and up to 7 years after closure for legal compliance</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: '#f5f5f5' }}>
                      <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>Competition Data</Typography>
                        <Typography variant="body2">Retained for 5 years for prize distribution and tax reporting purposes</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ backgroundColor: '#f5f5f5' }}>
                      <CardContent>
                        <Typography variant="h6" color="primary" gutterBottom>Marketing Data</Typography>
                        <Typography variant="body2">Retained until you unsubscribe or withdraw consent</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <Card sx={{ mb: 6, background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)' }}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" gutterBottom textAlign="center" color="primary">
                📞 Privacy Contact Information
              </Typography>
              <Typography variant="body1" paragraph textAlign="center">
                For any questions about this Privacy Policy or to exercise your privacy rights:
              </Typography>
              <Box textAlign="center">
                <Button 
                  variant="contained" 
                  onClick={() => navigate('/contact')}
                  sx={{ mr: 2, mb: 1 }}
                >
                  Contact Privacy Team
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={() => navigate('/terms-and-conditions')}
                  sx={{ mb: 1 }}
                >
                  View Terms & Conditions
                </Button>
              </Box>
              <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                We are committed to protecting your privacy and will respond to all inquiries within 30 days.
              </Typography>
            </CardContent>
          </Card>
        </motion.div>

        {/* Updates to Policy */}
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            📝 Policy Updates
          </Typography>
          <Typography variant="body2">
            We may update this Privacy Policy from time to time. When we make changes, we will notify you by 
            email and update the "Last Updated" date at the top of this page. Continued use of our platform 
            after changes constitutes acceptance of the updated policy.
          </Typography>
        </Alert>

        {/* Back to Registration */}
        <Box textAlign="center" sx={{ mb: 4 }}>
          <Button 
            variant="contained" 
            size="large"
            onClick={handleBackToRegister}
            sx={{ 
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem'
            }}
          >
            Back to Registration
          </Button>
        </Box>

        {/* Final Statement */}
        <Alert severity="success">
          <Typography variant="body1">
            <strong>Thank you for trusting us with your information.</strong> We are committed to maintaining 
            the highest standards of privacy protection and transparency. Your privacy is not just our policy—it's our promise.
          </Typography>
        </Alert>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage; 